import React, { useEffect, useState, useMemo } from 'react'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, FileText, Trash2 } from 'lucide-react'
import { apiGet, apiPost } from '../../lib/api'
import { CreateQuoteInput } from '@buddys-choice/shared'
import type { Product, CreateQuoteInput as CreateQuoteInputType } from '@buddys-choice/shared'
import { SearchableSelect } from '../../components/SearchableSelect'

export const AdminQuoteCreatePage: React.FC = () => {
  const navigate = useNavigate()
  const [products, setProducts] = useState<Product[]>([])
  const [submitError, setSubmitError] = useState('')

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateQuoteInputType>({
    resolver: zodResolver(CreateQuoteInput) as import('react-hook-form').Resolver<CreateQuoteInputType>,
    defaultValues: {
      customerName: '',
      customerPhone: '',
      customerEmail: '',
      notes: '',
      items: [],
    },
  })

  const { fields, append, remove } = useFieldArray({ control, name: 'items' })

  const productOptions = useMemo(
    () => products.map((p) => ({
      value: p.id,
      label: `${p.name} — ₹${p.basePrice}`,
      keywords: p.sku,
    })),
    [products],
  )

  useEffect(() => {
    apiGet<Product[]>('products', { isActive: true, pageSize: 100, sortBy: 'name', sortDir: 'asc' })
      .then((data) => {
        setProducts(data)
        if (data.length > 0) {
          const first = data[0]!
          append({
            productId: first.id,
            productName: first.name,
            unitPrice: first.basePrice,
            quantity: 1,
            discountPct: 0,
            sortOrder: 0,
          })
        }
      })
      .catch(console.error)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleAddItem = () => {
    const first = products[0]
    if (!first) return
    append({
      productId: first.id,
      productName: first.name,
      unitPrice: first.basePrice,
      quantity: 1,
      discountPct: 0,
      sortOrder: 0,
    })
  }

  const handleProductChange = (idx: number, productId: string) => {
    const product = products.find((p) => p.id === productId)
    if (!product) return
    setValue(`items.${idx}.productId`, product.id)
    setValue(`items.${idx}.productName`, product.name)
    setValue(`items.${idx}.unitPrice`, product.basePrice)
  }

  const handleCreateQuote = async (data: CreateQuoteInputType) => {
    setSubmitError('')
    try {
      await apiPost('quotes', {
        ...data,
        customerName: data.customerName.trim(),
        customerEmail: data.customerEmail?.trim() || undefined,
        notes: data.notes?.trim() || undefined,
      })
      navigate({ to: '/admin/quotes' })
    } catch (err: unknown) {
      setSubmitError((err as Error).message || 'Failed to create quote.')
    }
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <Link to="/admin/quotes" className="inline-flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--gold-main)]">
        <ArrowLeft className="w-4 h-4" /> Back to quotes
      </Link>

      <div className="glass-card p-6">
        <div className="flex items-center gap-2 mb-6">
          <FileText className="w-5 h-5 text-[var(--gold-dark)]" />
          <h1 className="text-xl font-serif font-bold">New B2B / custom quote</h1>
        </div>

        {submitError && <div className="alert alert-error text-xs mb-4">{submitError}</div>}

        <form onSubmit={handleSubmit(handleCreateQuote)} className="space-y-4 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="form-group">
              <label className="form-label">Customer name *</label>
              <input
                type="text"
                {...register('customerName')}
                placeholder="e.g. Mahalakshmi Jewellers"
                className="form-input"
                aria-invalid={!!errors.customerName}
              />
              {errors.customerName && <span className="form-error">{errors.customerName.message}</span>}
            </div>
            <div className="form-group">
              <label className="form-label">Customer phone *</label>
              <input
                type="tel"
                {...register('customerPhone', {
                  onChange: (e) => {
                    e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10)
                  },
                })}
                placeholder="9876543210"
                className="form-input font-mono"
                aria-invalid={!!errors.customerPhone}
              />
              {errors.customerPhone && <span className="form-error">{errors.customerPhone.message}</span>}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Email (optional)</label>
            <input
              type="email"
              {...register('customerEmail')}
              className="form-input"
            />
            {errors.customerEmail && <span className="form-error">{errors.customerEmail.message}</span>}
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label className="form-label font-bold text-[var(--gold-dark)]">Line items *</label>
              <button type="button" onClick={handleAddItem} className="text-xs text-[var(--gold-dark)] hover:underline" disabled={products.length === 0}>
                + Add item
              </button>
            </div>
            {errors.items && <span className="form-error">{errors.items.message}</span>}

            {fields.map((item, idx) => (
              <div key={item.id} className="p-3 rounded-lg bg-[var(--bg-surface-3)] border border-[var(--border)] space-y-2">
                <div className="flex justify-between items-center gap-2">
                  <Controller
                    control={control}
                    name={`items.${idx}.productId`}
                    render={({ field }) => (
                      <SearchableSelect
                        value={field.value}
                        onChange={(v) => handleProductChange(idx, v)}
                        options={productOptions}
                        placeholder="Select product"
                        aria-label="Quote line product"
                        className="flex-1"
                      />
                    )}
                  />
                  {fields.length > 1 && (
                    <button type="button" onClick={() => remove(idx)} className="text-red-400 p-1 hover:bg-red-500/10 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <span className="text-[10px] text-[var(--text-muted)] block">Unit price (₹)</span>
                    <input
                      type="number"
                      {...register(`items.${idx}.unitPrice`, { valueAsNumber: true })}
                      className="form-input py-1 text-xs font-mono"
                    />
                  </div>
                  <div>
                    <span className="text-[10px] text-[var(--text-muted)] block">Qty</span>
                    <input
                      type="number"
                      min={1}
                      {...register(`items.${idx}.quantity`, { valueAsNumber: true })}
                      className="form-input py-1 text-xs font-mono"
                    />
                  </div>
                  <div>
                    <span className="text-[10px] text-[var(--text-muted)] block">Discount %</span>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      {...register(`items.${idx}.discountPct`, { valueAsNumber: true })}
                      className="form-input py-1 text-xs font-mono"
                    />
                  </div>
                </div>
                {errors.items?.[idx] && (
                  <span className="form-error">
                    {errors.items[idx]?.unitPrice?.message ||
                      errors.items[idx]?.quantity?.message ||
                      errors.items[idx]?.discountPct?.message}
                  </span>
                )}
              </div>
            ))}
          </div>

          <div className="form-group">
            <label className="form-label">Notes / terms</label>
            <textarea {...register('notes')} rows={3} className="form-input" />
          </div>

          <div className="flex gap-3 pt-2">
            <Link to="/admin/quotes" className="btn btn-secondary flex-1 text-center">
              Cancel
            </Link>
            <button type="submit" disabled={isSubmitting || products.length === 0} className="btn btn-primary flex-1 font-semibold">
              {isSubmitting ? <span className="spinner" /> : 'Save draft quote'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
